import React, { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import Icon from '../icon'
import styles from './SelectionModal.module.scss'

/**
 * Two-column checkbox picker with a selection cap.
 *
 * Holds a *draft* of the selection: Cancel discards it, Done commits. That's
 * why the parent's value is only read when the modal opens — otherwise a
 * parent re-render mid-edit would clobber in-progress choices.
 */
export default function SelectionModal({
  show,
  onHide,
  onConfirm,
  title,
  description,
  options,
  value = [],
  max = 3,
  showIcons = false,
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value)
  const [isSaving, setIsSaving] = useState(false)

  // Re-seed the draft from the committed value each time the modal opens.
  // Deliberately keyed on `show` alone: including `value` would reset an
  // in-progress edit whenever the parent re-renders.
  useEffect(() => {
    if (show) setDraft(value)
  }, [show]) // eslint-disable-line

  const atLimit = draft.length >= max

  const toggle = id => {
    setDraft(prev => {
      if (prev.includes(id)) return prev.filter(v => v !== id)
      if (prev.length >= max) return prev
      return [...prev, id]
    })
  }

  // Await the parent's persist so the modal stays open (and the button stays
  // busy) until the save resolves. Closing optimistically would hide a failure.
  const handleConfirm = async () => {
    setIsSaving(true)
    try {
      const ok = await onConfirm(draft)
      if (ok !== false) onHide()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {description && <p className={styles.limit}>{description}</p>}
        <p className={`${styles.limit} ${atLimit ? styles.limitReached : ''}`}>
          {t('settings.selection.count', '{{count}} of {{max}} selected', {
            count: draft.length,
            max,
          })}
        </p>
        <div className={styles.grid}>
          {options.map(option => {
            const checked = draft.includes(option.id)
            const disabled = !checked && atLimit
            return (
              <label
                key={option.id}
                className={`${styles.option} ${
                  disabled ? styles.optionDisabled : ''
                }`}
              >
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(option.id)}
                />
                {showIcons && option.icon && (
                  <Icon name={option.icon} className={styles.optionIcon} />
                )}
                <span>{option.label}</span>
              </label>
            )
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onHide} disabled={isSaving}>
          {t('settings.selection.cancel', 'Cancel')}
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={isSaving}>
          {isSaving ? t('users.edit.saving') : t('users.edit.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

SelectionModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.node,
  description: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      icon: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.array,
  max: PropTypes.number,
  showIcons: PropTypes.bool,
}
